import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api.js";

export interface FunnelRow {
  adId: string;
  adName: string;
  campaignId: string | null;
  campaignName: string | null;
  adSet: string | null;
  platform: string | null;
  status: "active" | "paused";
  clicks: number;
  spend: number;
  conversations: number;
  orders: number;
  revenue: number;
  /** spend / conversations — 0 when no chats. */
  cpc: number;
  /** orders / conversations * 100 — 0 when no chats. */
  conv: number;
  roas: number | null;
}

export interface TrendWeek {
  weekLabel: string;
  weekStart: string;
  spend: number;
  revenue: number;
}

export interface PlatformAgg {
  platform: string;
  spend: number;
  revenue: number;
  chats: number;
  orders: number;
  roas: number | null;
}

export interface RankedAd {
  adId: string;
  adName: string;
  campaignName: string | null;
  platform: string | null;
  spend: number;
  revenue: number;
  roas: number;
}

export interface FunnelResponse {
  range: { from: string; to: string };
  totals: {
    clicks: number;
    spend: number;
    conversations: number;
    orders: number;
    revenue: number;
    roas: number | null;
  };
  rows: FunnelRow[];
  /** Weekly spend-vs-revenue series over the requested range. */
  trend: TrendWeek[];
  /** All-campaigns revenue-share snapshot — independent of the range. */
  byPlatform: PlatformAgg[];
  /** All-campaigns per-ad list (spend>0) ranked by ROAS desc. */
  rankedAds: RankedAd[];
}

export function useFunnel(businessId: string, from: string, to: string) {
  return useQuery<FunnelResponse>({
    queryKey: [businessId, "funnel", from, to],
    queryFn: () =>
      apiFetch(`/api/v1/campaigns/funnel?business=${businessId}&from=${from}&to=${to}`),
  });
}

/** Pull every WhatsApp-CTA ad (incl. paused/archived history) + 90d spend from Meta. */
export function useSyncAds(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ads: number; insights: number }>(`/api/v1/campaigns/sync?business=${businessId}`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [businessId, "funnel"] }),
  });
}
